import { useCallback, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import useLiveAudioTracklist from './useLiveAudioTracklist';
import useActiveEpisode from '@/hooks/audio/useActiveEpisode';
import useLiveAudioSeek from '@/hooks/audio/useLiveAudioSeek';
import CONFIG from '@/config/global';
import useGA from '@/hooks/ga/useGA';
import { truthyOrZero } from '@/helpers/math';
import useAudioReduxControls from '@/hooks/audio/useAudioReduxControls';
import { selectAudioReadyForSeek } from '@/state/selectors/audio';
import { GAuserSeekedAudio } from '@/helpers/events-ga/audio';
import { getTrackFromTracklistByTime } from '@/helpers/audio';

const {
  disableTrackSeek,
  snapSeekBackToTracklistThresholdSeconds,
  snapSeekForwardToTracklistThresholdSeconds
} = CONFIG;

const selector = (state) => ({ audioReadyForSeek: selectAudioReadyForSeek(state) });

const useMusicPlayerSeekerBar = () => {
  const { sendGAEvent } = useGA();

  // Hold the seek values as the user is moving the slider
  const [sliderMoveSeek, setSliderMoveInProgressSeek] = useState(null);

  const { audioReadyForSeek } = useSelector(selector, shallowEqual);
  const { episode } = useActiveEpisode();
  const {
    handleSkipTrack,
    currentTrack,
    currentTrackIndex,
    currentTrackText,
    currentTrackIsHidden,
    currentSeekedToTrack,
    nextTrack
  } = useLiveAudioTracklist();
  const { handleSaveSeek } = useAudioReduxControls();
  const { liveSeek, handleSeek: handleLiveSeek } = useLiveAudioSeek();
  const duration = episode?.duration || 0;

  // Calculate whether the display times should reference the actual audio seek, or slider movement
  const sliderMoveSeekNormalised = truthyOrZero(sliderMoveSeek) ? sliderMoveSeek : null;
  const seekNormalised = (duration ? liveSeek : 0);
  const currentSeek = (sliderMoveSeekNormalised !== null) ? sliderMoveSeek : seekNormalised;

  // Handle seek completion
  const handleSeek = useCallback((newSeek) => {
    const { track: newTracklistTrack } = getTrackFromTracklistByTime({
      episode,
      timeSeconds: newSeek
    });

    const { start_time: newTracklistTrackStartTime, end_time: newTracklistTrackEndTime } = newTracklistTrack || {};

    // Force the seek to sync to the start/end of the track if the user is within X seconds of the start/end
    let newSeekAdjusted = newSeek;
    let hasLocked = false;

    if (
      truthyOrZero(newTracklistTrackStartTime)
      && ((newSeek - newTracklistTrackStartTime) <= snapSeekBackToTracklistThresholdSeconds)
    ) {
      newSeekAdjusted = newTracklistTrackStartTime;
      hasLocked = true;
    } else if (
      truthyOrZero(newTracklistTrackEndTime)
      && ((newTracklistTrackEndTime - newSeek) <= snapSeekForwardToTracklistThresholdSeconds)
    ) {
      newSeekAdjusted = newTracklistTrackEndTime;
      hasLocked = true;
    }

    handleLiveSeek(newSeekAdjusted);
    handleSaveSeek(newSeekAdjusted);
    setSliderMoveInProgressSeek(null);

    const skippedTime = newSeek - liveSeek;
    const skippedTimeFormatted = Math.round(Math.abs(skippedTime));
    const seekDirection = skippedTime >= 0 ? 'forward' : 'back';
    const startPosition = Math.round(liveSeek);
    const endPosition = Math.round(newSeek);
    const snappedToPosition = Math.round(newSeekAdjusted);

    if (truthyOrZero(startPosition) && truthyOrZero(endPosition)) {
      sendGAEvent(GAuserSeekedAudio({
        seekDirection: skippedTimeFormatted ? seekDirection : undefined,
        skippedTime: skippedTimeFormatted,
        startPosition,
        endPosition,
        snappedToPosition: hasLocked ? snappedToPosition : undefined
      }));
    }
  }, [handleSaveSeek, handleLiveSeek, sendGAEvent, liveSeek, episode]);

  // Handle the slider movement event
  const handleMoveSlider = useCallback(({ seek: newSeek }) => {
    setSliderMoveInProgressSeek(newSeek || 0);
  }, []);

  return {
    handleMoveSlider,
    handleSeek,
    handleSkipTrack,
    disableSeek: !duration || disableTrackSeek || !audioReadyForSeek,
    current: currentSeek,
    currentPercentage: duration ? (currentSeek / duration) : 0,
    remaining: duration - currentSeek,
    currentTrack,
    currentTrackIndex,
    currentTrackText,
    currentTrackIsHidden,
    currentSeekedToTrack,
    nextTrack
  };
};

export default useMusicPlayerSeekerBar;